enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

// Define a base error interface that will work with both standard Error and custom AppError
interface ErrorLike {
  message: string;
  stack?: string;
  [key: string]: any; // Allow for additional properties
}

class Logger {
  private context: string;
  private level: LogLevel;

  constructor(context: string, level: LogLevel = LogLevel.INFO) {
      this.context = context;
      this.level = level;
  }

  private formatMessage(level: string, message: string, ...args: any[]): string {
      const timestamp = new Date().toISOString();
      const formattedMessage = args.length ? this.interpolate(message, args) : message;
      return `[${timestamp}] [${level}] ${this.context}: ${formattedMessage}`;
  }

  private interpolate(message: string, args: any[]): string {
      return message.replace(/%[sdjo]/g, (match: string) => {
          const arg = args.shift();
          switch (match) {
              case '%s': return String(arg);
              case '%d': return Number(arg).toString();
              case '%j': return JSON.stringify(arg);
              case '%o': return Object.prototype.toString.call(arg);
              default: return match;
          }
      });
  }

  private shouldLog(messageLevel: LogLevel): boolean {
      return messageLevel >= this.level;
  }

  debug(message: string, ...args: any[]): void {
      if (this.shouldLog(LogLevel.DEBUG)) {
          console.debug(this.formatMessage('DEBUG', message, ...args));
      }
  }

  info(message: string, ...args: any[]): void {
      if (this.shouldLog(LogLevel.INFO)) {
          console.info(this.formatMessage('INFO', message, ...args));
      }
  }

  warn(message: string, ...args: any[]): void {
      if (this.shouldLog(LogLevel.WARN)) {
          console.warn(this.formatMessage('WARN', message, ...args));
      }
  }

  error(message: string, error?: ErrorLike | unknown, ...args: any[]): void {
      if (this.shouldLog(LogLevel.ERROR)) {
          console.error(this.formatMessage('ERROR', message, ...args));
          
          // Handle various error types
          if (error) {
              if (typeof error === 'object' && error !== null) {
                  if ('stack' in error && error.stack) {
                      console.error(error.stack);
                  } else if ('message' in error) {
                      console.error(`Error details: ${(error as ErrorLike).message}`);
                  } else {
                      console.error('Error details:', error);
                  }
              } else {
                  console.error('Error details:', error);
              }
          }
      }
  }

  /**
   * Helper method to create a standardized error response
   * @param message The error message
   * @param error Optional error object
   * @returns An error object with a message property
   */
  createErrorResponse(message: string, error?: ErrorLike | unknown): { message: string } {
      // Log the error
      this.error(message, error);
      
      // If we have an error object with a message, use that
      if (error && typeof error === 'object' && 'message' in error) {
          return { message: (error as ErrorLike).message };
      }
      
      // Otherwise use the provided message
      return { message };
  }
}

export function getLogger(context: string): Logger {
  return new Logger(context, 
      process.env.NODE_ENV === 'production' ? LogLevel.INFO : LogLevel.DEBUG
  );
}